import React from 'react';

import {
  ImageNotFound01Icon,
  InformationCircleIcon,
  MoreHorizontalIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import StatusPill from 'components/Pills/StatusPills';

/**
 * MyComponent Props
 *
 * @param {Object} category - Object representing the category
 * @param {string} category.icon - Icon for the category (emoji or component)
 * @param {string} category.text - Text label for the category
 *
 * @param {string|null} title - Main title of the component
 * @param {string|null} description - Description text
 *
 * @param {Array<Object>} tags - Array of tag objects
 * @param {string} tags[].label - Label text for the tag
 * @param {string} tags[].type - Type of tag (e.g., 'success', 'warning', 'info')
 *
 * @param {string|null} subtext - Optional subtext
 * @param {string|null} note - Optional small note shown below subtext (e.g. explaining a disabled action)
 *
 * @param {Array<Object>} actions - Array of action objects
 * @param {string} actions[].label - Label for the action button
 * @param {function} actions[].onClick - Click handler for the action
 *
 * @param {string|null} className - Optional additional CSS classes
 * @param {boolean} disabled - Disable the component
 * @param {function} onClick - Click handler for the component
 */

export const ItemOverviewCard = ({
  category = {},
  title = null,
  description = null,
  tags = [],
  subtext = null,
  note = null,
  actions = [],
  className = null,
  disabled = false,
  image = null,
  displayNoImage = false,
  children = null,
  topRight = null,
  icon = MoreHorizontalIcon,
  onIconClick = null,
  onClick = () => {},
}) => (
  <div
    className={cn(
      'bg-white rounded-3xl shadow-sm p-8 w-full min-w-0 border border-neutral-4 flex flex-col gap-4 cursor-pointer hover:shadow-md transition-all duration-300 hover:border-primary-2',
      className,
      disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
    )}
    onClick={onClick}>
    {image ? (
      <div className="flex min-h-48 items-center justify-center border border-border-primary rounded-3xl bg-neutral-2">
        <img className="max-h-32 max-w-64 object-contain object-center" src={image} alt={title} />
      </div>
    ) : (
      displayNoImage && (
        <div className="flex min-h-48 items-center justify-center border border-border-primary rounded-3xl bg-neutral-2">
          <HugeiconsIcon
            icon={ImageNotFound01Icon}
            size="40"
            strokeWidth={1.5}
            className="text-neutral-6"
          />
        </div>
      )
    )}
    <div className="flex flex-wrap items-center justify-between align-start gap-2">
      {category && (
        <div className="flex items-center gap-2">
          {category.icon && (
            <HugeiconsIcon icon={category.icon} className="w-8 h-8 text-neutral-6" />
          )}
          {category.text && (
            <span className="text-lg font-regular text-neutral-6">{category.text}</span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 ml-auto">
        {topRight}
        <div className="flex items-center" onClick={e => e.stopPropagation()}>
          {actions?.length > 0 ? (
            <ActionsMenu
              newIconLibrary
              small
              triggerIcon={MoreHorizontalIcon}
              className="[&_svg]:text-neutral-6 [&_svg]:stroke-[2]">
              {actions.map((action, index) => (
                <ActionsMenu.Item
                  key={action.label ?? index}
                  title={action.label}
                  onClick={action.onClick}
                  icon={action.icon}
                  newIconLibrary
                  type={action.type ?? 'secondary'}
                  disabled={action.disabled}
                />
              ))}
            </ActionsMenu>
          ) : (
            onIconClick && (
              <HugeiconsIcon
                icon={icon}
                onClick={onIconClick}
                className="text-neutral-6 cursor-pointer"
                size={20}
                strokeWidth={2}
              />
            )
          )}
        </div>
      </div>
    </div>
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <span className="text-2xl font-medium text-neutral-7">{title}</span>
        <span
          className={`text-xl font-light line-clamp-2 overflow-hidden [&_p]:m-0 min-w-0  ${description ? 'text-neutral-6' : 'text-text-tertiary'}`}>
          {description ? description : 'No description available'}
        </span>
      </div>
    </div>
    {tags.length > 0 && (
      <div className="flex flex-wrap items-center gap-2">
        {tags.map((tag, i) =>
          React.isValidElement(tag) ? (
            <React.Fragment key={i}>{tag}</React.Fragment>
          ) : (
            <StatusPill key={tag.label} status={tag.type} text={tag.label} className="px-4" />
          ),
        )}
      </div>
    )}
    {children && <div>{children}</div>}
    {subtext && (
      <div className="flex items-center gap-2">
        <span className="text-lg font-light text-color-tertiary">{subtext}</span>
      </div>
    )}
    {note && (
      <div className="flex items-start gap-2 -mt-2">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={16}
          strokeWidth={2}
          className="text-neutral-5 shrink-0 mt-1"
        />
        <span className="text-lg font-light text-neutral-5">{note}</span>
      </div>
    )}
  </div>
);

ItemOverviewCard.propTypes = {
  category: PropTypes.shape({
    icon: PropTypes.any,
    text: PropTypes.string,
  }),
  title: PropTypes.string,
  description: PropTypes.string,
  tags: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.shape({
        label: PropTypes.string,
        type: PropTypes.string,
      }),
    ]),
  ),
  subtext: PropTypes.string,
  note: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      onClick: PropTypes.func,
      icon: PropTypes.any,
      type: PropTypes.string,
      disabled: PropTypes.bool,
    }),
  ),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};
